import { inject, Injectable } from '@angular/core'
import { AppError } from '@naturalcycles/js-lib'
import { CartDiscountType, DiscountCodeFM, Goal } from '@naturalcycles/shared'
import { sentry } from '@src/app/core/util/sentry.util.js'
import { SignalStore } from '../store/signalStore.js'
import { api } from './api.service.js'

@Injectable({ providedIn: 'root' })
export class DiscountCodeService {
  private store = inject(SignalStore)
  async persistDiscountCode(input: {
    code: string
    referral?: string
    forceRefresh?: boolean
  }): Promise<DiscountCodeFM> {
    const { code, referral, forceRefresh } = input
    const currentDiscountCode = this.store.$discountCode()
    if (currentDiscountCode && !forceRefresh) return currentDiscountCode

    const remoteDiscountCode = await this.getRemoteDiscountCodeMetadata(code)

    this.store.$discountCode.set(remoteDiscountCode)
    this.store.$referral.set(referral)

    return remoteDiscountCode
  }

  // TODO: Add support for setting the goal on the discount code in Airtable
  public async getGoalFromDiscount(discountCode: DiscountCodeFM): Promise<Goal> {
    const prefix = discountCode.code.substring(0, 2)
    if (prefix === 'PL') {
      return Goal.PLAN
    }
    return Goal.PREVENT
  }

  getCurrentReferralCode(): string | undefined {
    return this.store.$referral()
  }

  deleteCurrentDiscountCodeMetadata(): void {
    this.store.$discountCode.set(undefined)
  }

  /**
   * @description
   * Returns the current discount code metadata.
   *
   * @note
   * - if no code has been attempted to be set yet, it will return undefined
   * - if a code has been attempted to be set but does not exist, it will return { code, exists: false, type: CartDiscountType.CheckoutCode }
   * @returns {DiscountCode | undefined}
   */
  getCurrentDiscountCodeMetadata(): DiscountCodeFM | undefined {
    return this.store.$discountCode()
  }

  /**
   * @description
   * Fetches the discount code from airtable.
   * Note: Encrypted codes are a.k.a. "pharmacy codes"
   */
  private async getRemoteDiscountCodeMetadata(code: string): Promise<DiscountCodeFM> {
    try {
      const discountCode = await api.get<DiscountCodeFM>(`discountCodes/${code}`)
      return {
        ...discountCode,
        exists: true,
      }
    } catch (err) {
      if (!(err instanceof AppError && err.cause?.data.backendResponseStatusCode === 404)) {
        sentry.captureException(err)
      }
      return {
        code,
        exists: false,
        // Default to CheckoutCode when we don't know the type
        type: CartDiscountType.CheckoutCode,
      }
    }
  }
}
